import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useToast, writeWithToast } from '../common';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Shift } from './types';
import { sameWindow } from '../bookings/domain';
import type { DateWindow } from '../bookings/types';

// Shifts for a date window (the roster grid's week). Assign/remove are
// optimistic — the slot updates in place, the write persists in the background,
// and a reconcile refetch follows. The current week's shifts are held across a
// week-nav fetch so the grid never blanks. Revalidates on return to the page.
export function useShifts(from: string, to: string): {
  shifts: Shift[];
  // The window `shifts` are actually FOR — null before the first load lands.
  // The set is held across a week change (that is what stops the grid
  // blanking), so `loading` alone cannot tell a consumer whether the shifts on
  // hand belong to the week it is drawing. A consumer that draws a CONCLUSION
  // from them — "nobody is rostered" — needs this; the rota grid, which just
  // draws what it has, can ignore it. Mirrors useWindowedBookings.window.
  window: DateWindow | null;
  // The last attempt for this window failed. Only consumers that would
  // otherwise read a held or empty set as the week's own truth need it.
  failed: boolean;
  // First-load flag — true until the first successful fetch resolves, then false
  // forever (window changes hold the last set, never re-blank). Lets a consumer
  // show a skeleton on cold load without flashing on subsequent nav.
  loading: boolean;
  assign: (date: string, slot: number, staffId: string) => void;
  remove: (date: string, slot: number) => void;
} {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [window_, setWindow] = useState<DateWindow | null>(null);
  // The window whose fetch FAILED, not a bare flag. A boolean set on failure
  // is only ever cleared by a later success, so one bad week made every week
  // stepped to afterwards raise a failure banner instantly — before its own
  // request had said anything (PR #164, claude P2). Scoping the failure to the
  // window it happened in makes it expire on its own, the same way `window`
  // above tells a consumer which set it is holding.
  const [failedFor, setFailedFor] = useState<DateWindow | null>(null);
  const toast = useToast();
  // The shared SWR trigger core (lib/useFetchTrigger). This replaces a drifted
  // hand-rolled `nonce > 0 ? { cache: 'reload' } : {}`, under which one
  // reconcile() made EVERY later fetch (including plain week moves) bypass the
  // browser cache forever, while tick revalidations never bypassed it. Now a
  // reconcile bypasses once, a week move re-reads cacheable, and an on-return /
  // post-write revalidation bypasses — fetchPlan semantics, like every other hook.
  const trigger = useFetchTrigger();

  useEffect(() => {
    const run = trigger.begin();
    api<Shift[]>(`/shifts?from=${from}&to=${to}`, run.init)
      .then((body) => {
        if (!run.isCurrent()) return; // superseded by a newer week/refetch
        run.markContent();
        setShifts(body.data ?? []);
        setWindow({ from, to });
        setFailedFor(null);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return;
        // hold the last-known shifts on a failed fetch — but SAY that they are
        // the last-known ones. Silence here let a consumer read a held week as
        // the requested one (PR #164, codex P2).
        if (!run.isCurrent()) return;
        setFailedFor({ from, to });
      });
    return run.cancel;
  }, [from, to, trigger.nonce, trigger.tick]);

  // Bump to reconcile against the server after a write.
  const reconcile = trigger.reload;

  const assign = useCallback(
    (date: string, slot: number, staffId: string) => {
      const kind: 'am' | 'pm' = slot < 4 ? 'am' : 'pm';
      // optimistic: replace whatever was in (date, slot)
      setShifts((prev) => [
        ...prev.filter((s) => !(s.date === date && s.slot === slot)),
        { id: -1, date, slot, staffId, kind },
      ]);
      void writeWithToast(toast, {
        write: async () => {
          await api('/shifts', { method: 'PUT', body: { date, slot, staffId } });
          reconcile();
        },
        success: 'Rota updated',
        error: 'Could not update the rota.',
        revert: reconcile, // revert via refetch
      });
    },
    [reconcile, toast],
  );

  const remove = useCallback(
    (date: string, slot: number) => {
      setShifts((prev) => prev.filter((s) => !(s.date === date && s.slot === slot)));
      void writeWithToast(toast, {
        write: async () => {
          await api(`/shifts?date=${date}&slot=${slot}`, { method: 'DELETE' });
          reconcile();
        },
        success: 'Rota updated',
        error: 'Could not update the rota.',
        revert: reconcile,
      });
    },
    [reconcile, toast],
  );

  return { shifts, window: window_, failed: sameWindow(failedFor, { from, to }), loading: !loaded, assign, remove };
}
